import { z } from "zod";
import { BASE, ISO, VISIBILITY } from "./common.ts";

/** An asset class id (X11-530a): a slug from the agency's managed asset class list (settings assetClasses). The five built-in ids
 *  are its first entries and are never renamed; the list adds classes and archives them, but never removes an id. */
export const ASSET_CLASS_ID = z.string().regex(/^[a-z0-9][a-z0-9-]{0,59}$/);
export const FOLDER_KIND = z.enum(["client", "project", "template", "general"]);
/** A cloud app a folder or file is synced with (X5-17; Google Drive and OneDrive added in X5-29). */
export const CLOUD_PROVIDER = z.enum(["dropbox", "box", "google-drive", "onedrive"]);
/** A folder linked to a Dropbox, Box, Google Drive or OneDrive folder: its files are imported from there. `cursor` and `lastSync` are set by the sync. */
export const CLOUD_LINK = z.object({
  provider: CLOUD_PROVIDER,
  /** The integrations row (the connected app) the folder is read with. */
  integrationId: z.string().min(1),
  /** The remote folder: a Dropbox folder id or path ("" is the root), a Box folder id ("0" is the root), a Google Drive folder id ("root" is the root) or a OneDrive item id ("" is the root). */
  remoteId: z.string().max(500),
  remotePath: z.string().max(1000),
  cursor: z.string().optional(),
  lastSync: ISO.optional(),
  /** "copy" (the default) copies each file's bytes in; "link" takes only the file list, names, versions and web links (X11-538a). */
  mode: z.enum(["copy", "link"]).optional(),
  /** When true, a new version of a linked file is pushed to its cloud copy (X13-1204a). Absent or false: no push. */
  writeBack: z.boolean().optional(),
});
export type CloudLink = z.infer<typeof CLOUD_LINK>;
/** The folder as it exists in a connected cloud app, set when a provisioning run mirrors the client's tree (X13-0206a). `id` is a
 *  Dropbox folder path or a Google Drive folder id. */
export const FOLDER_REMOTE = z.object({ provider: CLOUD_PROVIDER, id: z.string().min(1).max(1000) });
export type FolderRemote = z.infer<typeof FOLDER_REMOTE>;
export const FOLDER = BASE.extend({
  name: z.string(),
  parentId: z.string().optional(),
  kind: FOLDER_KIND,
  clientId: z.string().optional(),
  projectId: z.string().optional(),
  cloudLink: CLOUD_LINK.optional(),
  remote: FOLDER_REMOTE.optional(),
  /** The asset class the folder carries (X11-530a): a class id of the managed list. Absent: none. */
  assetClass: ASSET_CLASS_ID.optional(),
});
export type Folder = z.infer<typeof FOLDER>;

export const FILE_TYPE = z.enum(["image", "pdf", "video", "audio", "doc", "sheet", "slides", "code", "design", "archive", "font"]);
/** The five built-in asset classes. Their ids are kept for good; the managed list (ASSET_CLASS_ID) holds them and any the agency adds. */
export const ASSET_CLASS = z.enum(["deliverable", "source", "brand", "contract", "internal"]);
export const PROOF_STATE = z.enum(["none", "in-review", "changes", "approved"]);
export const FILE_ITEM = BASE.extend({
  folderId: z.string(),
  name: z.string(),
  type: FILE_TYPE,
  /** Size in bytes. */
  size: z.number(),
  version: z.string(),
  versionCount: z.number(),
  /** A class id of the managed asset class list (X11-530a). The built-in five are its first entries. */
  assetClass: ASSET_CLASS_ID,
  usedIn: z.array(z.string()),
  ownerId: z.string(),
  url: z.string().optional(),
  proofState: PROOF_STATE,
  trashedAt: ISO.optional(),
  width: z.number().optional(),
  height: z.number().optional(),
  durationSec: z.number().optional(),
  // Alt text for an image (media library). Empty or missing means the image needs a description.
  alt: z.string().max(500).optional(),
  /** Free labels for the asset library (search and filters use them). Set by the team, never by a client. */
  tags: z.array(z.string().min(1).max(40)).optional(),
  /** When the licence to use this file ends. A reminder goes to its owner 30 days before, and again once it has passed. */
  usageRightsExpiresAt: ISO.nullable().optional(),
  /** The licence terms behind the rights date (for example "Web and print, EU only"). Set by the team, shown with the date. */
  rightsNote: z.string().max(300).nullable().optional(),
  /** The cloud file this one is synced from: its id, rev and web link at the last sync; `removed` once it has gone from the remote folder. */
  remote: z.object({
    provider: CLOUD_PROVIDER,
    id: z.string().min(1),
    rev: z.string().min(1),
    removed: z.boolean().optional(),
    link: z.string().max(2000).optional(),
    /** The outcome of the last write-back of this file (X13-1204a): pushed or skipped, the reason when skipped, and when. */
    push: z.object({ status: z.enum(["pushed", "skipped"]), reason: z.string().max(500).optional(), at: ISO, version: z.number().optional() }).optional(),
  }).optional(),
  /** Where the bytes are: absent or "blob" for the blob store; "remote" for a link-only cloud file, which has no bytes here (its `url` is its web link). */
  storage: z.enum(["blob", "remote"]).optional(),
  /** Set by the team. A client sees the file in the portal only when this is true. */
  clientVisible: z.boolean().optional(),
  /** The Internal or Shared marker (X13-1902a). Computed by the server from the live client grants on the file and its folders,
   *  never set by a request; it is a label and never grants access. Absent on a write: the server sets it to internal or computes it.
   *  Stored NOT NULL with default internal (R-x13-visibility.sql), so the default is the stored shape. */
  visibility: VISIBILITY.default("internal"),
});
export type FileItem = z.infer<typeof FILE_ITEM>;

/** Who a file or folder grant applies to: one person, a preset role, or a client's portal team. */
export const GRANT_PRINCIPAL = z.enum(["person", "role", "client"]);
export const GRANT_LEVEL = z.enum(["view", "comment", "edit", "full"]);
/** A per-file or per-folder access grant, folded into the CASL conditions (PLAN section 9). Set `fileId` or `folderId`. */
export const FILE_GRANT = z.object({
  id: z.string(),
  fileId: z.string().optional(),
  folderId: z.string().optional(),
  principalType: GRANT_PRINCIPAL,
  principalId: z.string(),
  level: GRANT_LEVEL,
  /** When the access ends (X2-05). A grant past this time opens nothing; a daily job removes it. Absent: it does not end. */
  expiresAt: ISO.optional(),
});
export type FileGrant = z.infer<typeof FILE_GRANT>;

/** One stored version of a file. `blobHash` is the lowercase hex SHA-256 of the content (content-addressed). */
export const FILE_VERSION = z.object({
  id: z.string(),
  fileId: z.string(),
  /** Version number, 1-based. */
  n: z.number(),
  blobHash: z.string().regex(/^[0-9a-f]{64}$/),
  byId: z.string(),
  note: z.string().optional(),
  createdAt: ISO,
});
export type FileVersion = z.infer<typeof FILE_VERSION>;

export const PROOF_VERSION = z.object({ id: z.string(), label: z.string(), at: ISO, url: z.string() });
export const PIN_REPLY = z.object({ id: z.string(), author: z.string(), body: z.string(), at: ISO });
export const PIN = z.object({
  id: z.string(),
  n: z.number(),
  /** Position on the file, 0 to 1 from the left and top edges. */
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
  page: z.number().optional(),
  timeSec: z.number().optional(),
  authorId: z.string().optional(),
  contactName: z.string().optional(),
  body: z.string(),
  at: ISO,
  resolved: z.boolean(),
  replies: z.array(PIN_REPLY),
});
export const PROOF_APPROVAL = z.object({ name: z.string(), state: z.enum(["pending", "approved", "changes"]), at: ISO.optional() });
export const PROOF = BASE.extend({
  fileId: z.string(),
  title: z.string(),
  projectId: z.string(),
  clientId: z.string(),
  round: z.number(),
  /** Revision rounds included in the project. */
  allowance: z.number(),
  state: z.enum(["in-review", "changes", "approved"]),
  dueAt: ISO,
  /** The note recorded with the latest approval or change request. */
  decisionNote: z.string().optional(),
  versions: z.array(PROOF_VERSION),
  pins: z.array(PIN),
  approvals: z.array(PROOF_APPROVAL),
});
export type Proof = z.infer<typeof PROOF>;

/** The page a reviewer pinned on a live site, sent by the feedback widget (X14-0213a). Only these fields are stored: the address
 *  as typed (the server never fetches it), the browser name, the screen and viewport sizes, and the id of a screenshot blob. */
const PAGE_SIZE = z.object({ width: z.number().int().min(1).max(10000), height: z.number().int().min(1).max(10000) });
export const PAGE_CONTEXT = z.object({
  url: z.string().max(2000),
  browser: z.string().max(120),
  screen: PAGE_SIZE,
  viewport: PAGE_SIZE,
  screenshotBlobId: z.string().optional(),
});
export type PageContext = z.infer<typeof PAGE_CONTEXT>;

/** A reply on a normalised proof pin. `authorId` is a person. */
export const PROOF_PIN_REPLY = z.object({ id: z.string(), authorId: z.string(), body: z.string(), at: ISO });
/** A proof comment as its own row, with status and replies. `version` is the proof version number, 1-based. */
export const PROOF_PIN = z.object({
  id: z.string(),
  proofId: z.string(),
  version: z.number(),
  /** Position on the file, 0 to 1 from the left and top edges. */
  x: z.number().min(0).max(1),
  y: z.number().min(0).max(1),
  /** Seconds into a video or audio proof; never before the start. */
  t: z.number().min(0).optional(),
  page: z.number().optional(),
  authorId: z.string(),
  authorType: z.enum(["person", "contact"]),
  body: z.string(),
  status: z.enum(["open", "resolved"]),
  replies: z.array(PROOF_PIN_REPLY),
  /** The task made from this comment (POST /proofPins/:id/task), once made. */
  taskId: z.string().optional(),
  /** The live-site context a feedback widget sent with the pin (X14-0213a). Absent for pins made in the app. */
  pageContext: PAGE_CONTEXT.optional(),
});
export type ProofPin = z.infer<typeof PROOF_PIN>;

/** An SMS sender ID (X13-1001a, widened 2026-10-26): 1 to 11 characters of letters, digits, spaces and . - + _ &, with at least one
 *  letter and no leading or trailing space. This is the AlphaSender set Twilio lists; a period works in some countries only. */
export const SMS_SENDER_RE = /^(?=[A-Za-z0-9 ._+&-]*[A-Za-z])(?! )[A-Za-z0-9 ._+&-]{1,11}(?<! )$/;

export const BRAND_KIT = BASE.extend({
  clientId: z.string(),
  /** `value` is a CSS colour string; this is client brand data. */
  colors: z.array(z.object({ name: z.string(), value: z.string() })),
  fonts: z.array(z.object({ name: z.string(), role: z.string() })),
  logoFileIds: z.array(z.string()),
  voice: z.string(),
  guidelinesDocId: z.string().optional(),
});
export type BrandKit = z.infer<typeof BRAND_KIT>;

export const SHARE_LINK = BASE.extend({
  fileId: z.string().optional(),
  folderId: z.string().optional(),
  url: z.string(),
  password: z.boolean(),
  /** True when the link is protected by a password (stored hashed, never returned). */
  passwordSet: z.boolean(),
  expiresAt: ISO.optional(),
  /** Access cap; the link stops working once `uses` reaches it. */
  maxUses: z.number().optional(),
  uses: z.number(),
  allowDownload: z.boolean(),
  watermark: z.boolean(),
  views: z.number(),
  createdBy: z.string(),
});
export type ShareLink = z.infer<typeof SHARE_LINK>;
